/**
 * PATH: packages/email-hub/src/feature/lib/emailCapability.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-EMAILCAPABILITY-001
 * PURPOSE: Canonical body of this email lib module. Lifted
 *          from apps/<tenant>/src/features/email/lib/emailCapability.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/lib/emailCapability.ts: thin re-export
 *     shim that points back here.
 */

/** Minimum shape this helper needs from `OutlookStatus`. We deliberately
 *  use a structural type instead of importing `OutlookStatus` so the
 *  helper can stay in `lib/` without pulling the heavy hooks file. */
export interface ProviderConnectionBits {
  connected: boolean | null
  isExpired?: boolean
  reachable?: boolean
  orgConnected?: boolean
}

/** Minimum shape we need from `EmailAccount` for the capability check. */
export interface SharedMailboxBits {
  /** Whether the row is sync-enabled. `false` rows count as "present but
   *  paused" — we still treat the user as capable; the row exists so the
   *  send path is wired. */
  sync_enabled?: boolean
}

/**
 * `canSendEmail({ outlook, gmail, sharedMailboxes })` →
 *   true iff the user has at least one working path to send email:
 *     - personal Outlook OAuth, OR
 *     - personal Gmail OAuth, OR
 *     - org-level Outlook fallback (any user in org has a token), OR
 *     - org-level Gmail fallback, OR
 *     - at least one shared mailbox grant in /api/email-accounts/.
 *
 * Status-fetch failure (reachable === false) does NOT count as broken
 * because the most common cause is a transient network blip; the existing
 * banner-tone-down for `'unreachable'` already covers that with a softer
 * message.
 */
export function canSendEmail(input: {
  outlook?: ProviderConnectionBits | null
  gmail?: ProviderConnectionBits | null
  sharedMailboxes?: SharedMailboxBits[] | null
}): boolean {
  const { outlook, gmail, sharedMailboxes } = input

  if (outlook) {
    if (outlook.connected === true && !outlook.isExpired) return true
    if (outlook.orgConnected === true) return true
  }
  if (gmail) {
    if (gmail.connected === true && !gmail.isExpired) return true
    if (gmail.orgConnected === true) return true
  }
  if (sharedMailboxes && sharedMailboxes.length > 0) return true

  return false
}

/**
 * `shouldShowConnectBanner(input)` →
 *   true iff the proactive, always-on "Connect your email" page banner
 *   should render.
 *
 * This is the single gate for the EmailLayout top banner. The banner is a
 * NAG that claims "SMTP fallback is used until you connect", so it must
 * ONLY appear when there is genuinely NO working send path —
 * `canSendEmail` already encodes every fallback (personal OAuth, an
 * org-level token from another user in the org, or a shared-mailbox
 * grant).
 *
 * BUG-2026-06-15-EMAIL-CONNECT-BANNER-IGNORES-ORG: the previous gate
 * hand-rolled an Outlook-only check (`outlookConnected === false || …`)
 * and was blind to org connectivity, so an operator/partner viewing a
 * fully-synced Gmail-native org (Newmark — Mark's Gmail attached at the
 * org level, 158k emails flowing) saw the false alarm. Routing the gate
 * through `canSendEmail` kills the whole class of "banner lies about send
 * capability" bugs (Mark 2026-05-13, now 2026-06-15).
 *
 * New-user onboarding (Mitch, BUG-2026-05-28) is NOT served by this
 * proactive nag — it is served by the sidebar's always-available
 * "Connect your mailbox" buttons + "Add mailbox" affordance, which do
 * not gate on `canSendEmail`. Suppressing this banner therefore does not
 * lock anyone out of connecting their own mailbox.
 *
 * Returns `false` while either provider status is still loading
 * (`*Loaded === false`) so a disconnected-looking default state can't
 * flash the banner before the org token resolves.
 */
export function shouldShowConnectBanner(input: {
  /** Master Quotech session dead — the QuotechReconnectBanner owns that
   *  recovery UI, so this banner must stand down to avoid stacked alarms. */
  quotechAuthLost: boolean
  /** True once `/api/outlook/status/` has produced a real answer. */
  outlookLoaded: boolean
  /** True once `/api/gmail/status/` has produced a real answer. */
  gmailLoaded: boolean
  outlook?: ProviderConnectionBits | null
  gmail?: ProviderConnectionBits | null
  sharedMailboxes?: SharedMailboxBits[] | null
}): boolean {
  const { quotechAuthLost, outlookLoaded, gmailLoaded } = input
  if (quotechAuthLost) return false
  // Avoid a flash of "Connect your email" before both providers' status
  // has resolved — the hooks default to a disconnected-looking state.
  if (!outlookLoaded || !gmailLoaded) return false
  return !canSendEmail(input)
}

/**
 * `shouldShowOutlookNag(outlook, sharedMailboxes)` →
 *   true iff the per-page Outlook banner should render at all.
 *
 * Wraps `canSendEmail` with an Outlook-specific lens — we still show the
 * banner in cases where Outlook *itself* is the broken provider AND there
 * is no working fallback. If the user has any shared mailbox OR Gmail OR
 * the org token, we suppress.
 *
 * Returning `null` means "we don't know yet" (status still loading) — the
 * caller should fall back to its previous behaviour (typically: hide).
 */
export function shouldShowOutlookNag(
  outlook: ProviderConnectionBits | null | undefined,
  sharedMailboxes: SharedMailboxBits[] | null | undefined,
): boolean | null {
  if (!outlook) return null
  if (outlook.connected === null) return null
  if (canSendEmail({ outlook, sharedMailboxes })) return false
  return outlook.connected === false || !!outlook.isExpired || outlook.reachable === false
}
